import React, { useRef, useEffect, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import formStyles from '../../styles/SmartSendForm.module.css';
import {
  getResendCooldownRemaining,
  saveResendCooldown,
  getResendCooldownDuration,
} from '../../utils/localStorage';

/**
 * CodeVerificationStep - Step 2: 6-digit SMS code verification
 * Displays 6 input boxes for code entry with auto-advance and auto-submit
 * Desktop users: Auto-advance to delivery form after verification
 * Mobile users: Show "Redeem in Gesture App" or "Skip" buttons
 * @param {string} phoneNumber - Phone number that received the code
 * @param {Function} onVerify - Handler for code verification
 * @param {Function} onRedeemInApp - Handler for "Redeem in Gesture App" action
 * @param {Function} onSkip - Handler for skipping verification
 * @param {Function} onResendCode - Handler for resending verification code
 * @param {Function} onUseDifferentPhone - Handler for "Use a different phone number" action
 * @param {boolean} isLoadingDeepLink - Loading state for deep link generation
 * @param {boolean} isVerifyingCode - Loading state during code verification
 * @param {boolean} isCodeVerified - Success state after verification
 * @param {boolean} isDesktop - Device type for conditional flow (desktop auto-advances, mobile shows buttons)
 * @param {string} orderId - SmartSend order identifier for scoping cooldown
 */
const CODE_LENGTH = 6;
const AUTO_SUBMIT_DELAY = 100;

const CodeVerificationStep = ({
  phoneNumber,
  onVerify,
  onRedeemInApp,
  onSkip,
  onResendCode,
  onUseDifferentPhone,
  isLoadingDeepLink = false,
  isVerifyingCode = false,
  isCodeVerified = false,
  isDesktop = false, // Default to false (mobile) - must be explicitly passed from context
  orderId,
}) => {
  const inputRefs = useRef([]);
  const [cooldownSeconds, setCooldownSeconds] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const prevVerifyingRef = useRef(isVerifyingCode);

  useEffect(() => {
    // Auto-focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // Check initial cooldown state
    if (!orderId) return;
    const remaining = getResendCooldownRemaining(orderId);
    if (remaining !== null) {
      setCooldownSeconds(remaining);
    }
  }, [orderId]);

  // Clear inputs on verification failure
  useEffect(() => {
    // Detect when verification completes unsuccessfully
    // (isVerifyingCode changed from true to false, but isCodeVerified is still false)
    if (
      prevVerifyingRef.current === true &&
      isVerifyingCode === false &&
      !isCodeVerified
    ) {
      // Clear all input fields
      inputRefs.current.forEach((input) => {
        if (input) {
          input.value = '';
        }
      });

      // Focus first input for immediate retry
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }

    // Update ref for next comparison
    prevVerifyingRef.current = isVerifyingCode;
  }, [isVerifyingCode, isCodeVerified]);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds === null || cooldownSeconds <= 0 || !orderId) {
      setCooldownSeconds(null);
      return;
    }

    const timer = setInterval(() => {
      const remaining = getResendCooldownRemaining(orderId);
      if (remaining === null || remaining <= 0) {
        setCooldownSeconds(null);
      } else {
        setCooldownSeconds(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds, orderId]);

  /**
   * Gets the current code from all input fields
   * @returns {string} The 6-digit code or empty string
   */
  const getCurrentCode = () => {
    return inputRefs.current.map((input) => input?.value || '').join('');
  };

  /**
   * Checks if all inputs are filled and auto-submits if ready
   */
  const checkAndSubmitCode = () => {
    const code = getCurrentCode();
    if (code.length === CODE_LENGTH && !isVerifyingCode && !isCodeVerified) {
      setTimeout(() => {
        onVerify(code);
      }, AUTO_SUBMIT_DELAY);
    }
  };

  /**
   * Fills input fields with digits starting from the specified index
   * @param {number} startIndex - Index to start filling from
   * @param {string} digits - String of digits to fill
   */
  const fillInputsWithDigits = (startIndex, digits) => {
    digits.split('').forEach((digit, i) => {
      if (inputRefs.current[startIndex + i]) {
        inputRefs.current[startIndex + i].value = digit;
      }
    });

    // Focus the next empty input or the last input if all filled
    const nextIndex = Math.min(startIndex + digits.length, CODE_LENGTH - 1);
    if (inputRefs.current[nextIndex]) {
      inputRefs.current[nextIndex].focus();
    }
  };

  const handleInputChange = (index, value) => {
    // Handle autofill scenarios where multiple digits might be entered at once
    const numericValue = value.replace(/\D/g, '');

    // If multiple digits detected (autofill scenario), distribute them
    if (numericValue.length > 1) {
      const digits = numericValue.slice(0, CODE_LENGTH);
      fillInputsWithDigits(index, digits);
      checkAndSubmitCode();
      return;
    }

    // Single digit input (normal typing)
    const singleDigit = numericValue.slice(0, 1);
    if (singleDigit && inputRefs.current[index]) {
      inputRefs.current[index].value = singleDigit;

      // Auto-advance to next input
      if (index < CODE_LENGTH - 1 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }

      checkAndSubmitCode();
    }
  };

  /**
   * Handles paste events and Chrome SMS autofill
   * Distributes pasted/autofilled code across all input fields starting from the first
   * @param {ClipboardEvent} e - Paste event
   */
  const handlePaste = (e) => {
    e.preventDefault();

    // Safely get clipboard data with fallback
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) {
      return;
    }

    const pastedData = clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);

    if (pastedData.length > 0) {
      // Always start from the first input when pasting
      fillInputsWithDigits(0, pastedData);
      checkAndSubmitCode();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to go to previous input
    if (
      e.key === 'Backspace' &&
      !inputRefs.current[index]?.value &&
      index > 0
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleRedeemInApp = () => {
    if (onRedeemInApp) {
      onRedeemInApp();
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  const handleResendCode = async () => {
    if (!onResendCode || cooldownSeconds !== null || isResending) {
      return;
    }

    setIsResending(true);
    try {
      // Call parent handler to resend code
      const success = await onResendCode();

      if (success && orderId) {
        // Save cooldown timestamp
        saveResendCooldown(orderId);
        // Set initial cooldown state (environment-based: 10s dev, 300s prod)
        setCooldownSeconds(getResendCooldownDuration());
      }
    } catch (error) {
      console.error('Error resending code:', error);
    } finally {
      setIsResending(false);
    }
  };

  /**
   * Format cooldown time as MM:SS
   * @param {number} seconds - Seconds remaining
   * @returns {string} Formatted time string
   */
  const formatCooldownTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={formStyles.verificationCard}
      role="form"
      aria-label="SMS code verification"
    >
      {!isCodeVerified ? (
        <>
          <label className={formStyles.label} id="code-label">
            Enter Code
          </label>
          <div
            className={formStyles.otp}
            role="group"
            aria-labelledby="code-label"
            aria-describedby="code-help"
          >
            {Array.from({ length: CODE_LENGTH }, (_, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                inputMode="numeric"
                autoComplete="one-time-code"
                className={formStyles.otpInput}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isVerifyingCode}
                aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
                aria-required="true"
                type="text"
                pattern="[0-9]"
              />
            ))}
          </div>
          <p className={formStyles.note} id="code-help">
            We sent a 6-digit code to {phoneNumber}
          </p>

          {/* Use a different phone number link */}
          {onUseDifferentPhone && (
            <div className={formStyles.phoneChangeContainer}>
              <button
                type="button"
                className={formStyles.phoneChangeLink}
                onClick={onUseDifferentPhone}
                disabled={isVerifyingCode}
                aria-label="Use a different phone number"
              >
                Use a different phone number
              </button>
            </div>
          )}

          {/* Resend Code Section */}
          <div className={formStyles.resendContainer}>
            <p className={formStyles.resendText}>
              Didn't receive a code?{' '}
              {cooldownSeconds !== null ? (
                <span
                  className={formStyles.resendTimer}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  Resend in {formatCooldownTime(cooldownSeconds)}
                </span>
              ) : (
                <button
                  type="button"
                  className={formStyles.resendLink}
                  onClick={handleResendCode}
                  disabled={isResending || isVerifyingCode}
                  aria-label="Resend verification code"
                >
                  {isResending ? 'Sending...' : 'Resend'}
                </button>
              )}
            </p>
          </div>

          {isVerifyingCode && (
            <div
              className={formStyles.verifyingContainer}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              <ClipLoader color={'#1792e3'} size={20} aria-hidden="true" />
              <span className={formStyles.verifyingText}>
                Verifying code...
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Green checkmark */}
          <div
            className={formStyles.verifiedContainer}
            role="status"
            aria-live="polite"
            aria-label="Verification successful"
          >
            <div className={formStyles.verifiedCheckmark}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>

          {/* Verified message - different for desktop vs mobile */}
          <p className={formStyles.verifiedMessage} id="verified-message">
            {isDesktop
              ? "You're all set! Taking you to delivery details..."
              : 'Phone number verified'}
          </p>

          {/* Action buttons - only show for mobile users */}
          {/* Conditional: !isDesktop means "if NOT desktop, show buttons" */}
          {/* CSS media query (.mobileOnlyButton) also hides these on desktop as defensive fallback */}
          {!isDesktop && (
            <>
              <button
                className={`${formStyles.button} ${formStyles.mobileOnlyButton} ${formStyles.redeemInAppButton}`}
                onClick={handleRedeemInApp}
                disabled={isLoadingDeepLink}
                aria-label="Open in the Gesture app to continue"
                aria-busy={isLoadingDeepLink}
                aria-describedby="verified-message"
              >
                {isLoadingDeepLink
                  ? 'Opening app...'
                  : 'Redeem in the Gesture App'}
              </button>
              <button
                className={`${formStyles.skipButton} ${formStyles.mobileOnlyButton}`}
                onClick={handleSkip}
                aria-label="Skip and continue in browser"
              >
                Skip
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CodeVerificationStep;
